/* eslint-disable no-param-reassign */
export const attrPieceCount = 'pieceCount';
export const attrPlanMin = 'plannedMinutes';
export const attrUplanMin = 'unplannedStoppedMinutes';
export const attrPStopMin = 'plannedStoppedMinutes';
export const attrTargetRate = 'targetRateAccumulator';
export const attrRunRate = 'runRateAccumulator';
export const attrReport = 'reportedMinutes';
export const attrCount = 'count';
export const attrCurrentRunCount = 'currentRunCount';
export const attrBadPieceCount = 'badPieceCount';
export const attrCurrentTargetCount = 'currentTargetCount';
export const attrMinuteCount = 'estDiff';
export const attrScheduledRunMinutes = 'scheduledRunMinutes';
export const attrIngestVersion = 'ingestVersion';

// All attributes from printer metrics
export const attributes = [
  attrPieceCount,
  attrPlanMin,
  attrUplanMin,
  attrPStopMin,
  attrTargetRate,
  attrRunRate,
  attrReport,
  attrScheduledRunMinutes
];

// All oee object agg attributes for recalc
export const oeeAttributes = [
  attrCount,
  attrPlanMin,
  attrUplanMin,
  attrPStopMin,
  attrReport,
  attrCurrentRunCount,
  attrBadPieceCount,
  attrCurrentTargetCount,
  attrScheduledRunMinutes
];


/**
 * Totals printer metric data into aggregate summary
 * @param metrics array of objects with (@see attributes)
 * @param _attributes
 * @params _attributes property names to sum, defaults to exported (@see attributes)
 * @return summation map
 */

export function aggregateMetrics(metrics, _attributes = attributes) {
  // collect type attribute for slices
  const sliceTypes = {};

  const agg = metrics.reduce(
    (sums, metric) => {
      // add every attribute to running total
      _attributes.forEach((attr) => {
        sums[attr] += metric[attr] || 0;
      });

      if (metric.sliceKeyIds != null || metric.sliceKeys != null) {
        // If sliceKeyIds is present, it is a DynoDB set of string IDs; assume it has slice attributes.
        // Otherwise the metric object is a base entity and sliceKeys is a simple array of sliceKey objects,
        // i.e. minute data belonging to slices, not sliced itself.
        const baseSlices = metric.sliceKeyIds == null;
        const getKeyId = key => ((baseSlices) ? key.id : key); // helper to switch between key objects and key IDs

        // get the correct id values, and create any intersections
        const skIds = (baseSlices) ? metric.sliceKeys : metric.sliceKeyIds.values;
        if (skIds.length > 1) {
          const intersectionKey = skIds.map(key => getKeyId(key)).sort().join('_');
          skIds.push(baseSlices ? { id: intersectionKey, type: 'intersection' } : intersectionKey);
          sliceTypes[intersectionKey] = 'intersection';
        }

        // for every slice, add to or start running total for every sliced attribute
        for (const key of skIds) {
          const keyId = getKeyId(key);

          _attributes.forEach((attr) => {
            const kattr = `${keyId}_${attr}`;
            sums[kattr] = (sums[kattr] || 0) + (metric[(baseSlices) ? attr : kattr] || 0);
          });
          if (keyId != null) sliceTypes[keyId] = (baseSlices) ? key.type : metric[`${key}_type`];
        }
      }

      if (metric.slices != null) {
        sums.slices = (sums.slices || {});
        // If slices is present, it is a calculated slice, agg the results based on sliceKey and carry over the type
        for (let i = 0; i < metric.slices.length; i++) {
          const slice = metric.slices[i];
          const sumSlice = sums.slices[slice.sliceKey] || (({ sliceKey, type }) => ({ sliceKey, type }))(slice);
          _attributes.forEach((attr) => {
            sumSlice[attr] = (sumSlice[attr] || 0) + slice[attr];
          });
          sums.slices[slice.sliceKey] = sumSlice;
        }
      }

      return ({...sums, ...(metric.ingestVersion != null ? {ingestVersion: metric.ingestVersion} : {})}); // return accumulator
    }, _attributes.reduce((iO, attr) => { iO[attr] = 0; return iO; }, {}),
  );

  // preserve slice type attributes and aggregate unique slice key IDs to a list
  if (agg.slices === undefined) {
    // Mixing 'slices' collection and 'sliceKeys' attributes is not expected
    agg.sliceKeyIdList = Object.keys(sliceTypes);
    for (const id of agg.sliceKeyIdList) agg[`${id}_type`] = sliceTypes[id];
  }
  return agg;
}

export function lastScheduledRunMinutes(metrics){
  const msrm = metrics.reduce((agg, m) => {
    if(m.sliceKeys) m.sliceKeys.filter(sk => Number.isSafeInteger(sk.scheduledRunMinutes))
      .forEach( sk  => agg[`${sk.id}_scheduledRunMinutes`] = sk.scheduledRunMinutes);
    return agg;
  }, {});

  const values = Object.values(msrm);

  if(values.length)
    msrm.scheduledRunMinutes = values.reduce((sum, i) => sum + i);

  return msrm;
}

export class OeeUtil {
  /**
   * Performance =
   *   Total Count / Run Time / Ideal Run Rate
   *   count / (plannedTime - unplannedStopTime) / Ideal Run Rate
   * @param totalCount actual product count
   * @param runTime actual operating time
   * @param totalTime used to calculate ideal run rate
   * @param currentTargetCount used to calculate ideal run rate
   * @param ingestVersion (v2 ingest will update rates only if supposed to be printing)
   * @param plannedTime this is reported minutes minus the planned stop minutes. Should equal scheduled run minutes, if no disconnects.
   * @returns {number}
   */
  static getPerformance(
    { totalCount, runTime, currentTargetCount, totalTime, ingestVersion, plannedTime },
  ) {
    if(ingestVersion === 2)
    {
      if(currentTargetCount > 0 && plannedTime > 0) {
        const ppm = currentTargetCount / plannedTime; //?
        const targetActual = ppm * runTime; //?
        return totalCount / targetActual;
      }
      // DE-1639 sometimes "bad" slices occur and we don't know how to reconcile when we were supposed
      // to be running and have no target (or vice versa)
      return 0;
    }
    // since we're operating on minutes normalize to from hour rate
    const idealRunRate = currentTargetCount / totalTime;
    return (totalCount / runTime) / idealRunRate;
  }

  /**
   * Availability
   *   all actual running time over the planned running time
   *   all units are minutes.
   * @param totalTime used to calculate planned running time
   * @param runTime actual operating time
   * @param plannedStopTime used to calculate planned running time
   * @returns {number}
   */
  static getAvailability(totalTime, runTime, plannedStopTime) {
    if (totalTime !== plannedStopTime) return (runTime / (totalTime - plannedStopTime));
    return 0;
  }

  static getRecordAvailability(record){
    return record.scheduledRunMinutes
      // planned stop does not get accumulated in scheduleRunMinutes
      ? OeeUtil.getAvailability(record.scheduledRunMinutes, record.plannedMinutes, 0)
      : OeeUtil.getAvailability(record.reportedMinutes, record.plannedMinutes, record.plannedStoppedMinutes);
  }

  /**
   * Quality
   *   all good pieces over all pieces
   *   (expressed by tracking bad pieces)
   * @param totalCount
   * @param badPieceCount
   * @returns {number}
   */
  static getQuality(totalCount, badPieceCount) {
    if (totalCount) return (totalCount - badPieceCount) / totalCount;
    return 1;
  }
}

/**
 * @param count
 * @param {number|*} plannedMinutes
 * @param {number|*} unplannedStoppedMinutes
 * @param {*|number} plannedStoppedMinutes
 * @param {number|*} reportedMinutes
 * @param currentTargetCount
 * @param currentRunCount
 * @return map input params with OEE, ready for DB insert
 */
export function OeeRecord(parameters) {
  const {
    count, plannedMinutes, unplannedStoppedMinutes, plannedStoppedMinutes, reportedMinutes, currentTargetCount, currentRunCount, badPieceCount, ingestVersion, scheduledRunMinutes
  } = parameters;
  // straight record copy
  const record = {
    count,
    plannedMinutes,
    unplannedStoppedMinutes: unplannedStoppedMinutes || 0,
    plannedStoppedMinutes: plannedStoppedMinutes || 0,
    reportedMinutes,
    currentTargetCount,
    currentRunCount,
    badPieceCount: badPieceCount || 0,
    ingestVersion: ingestVersion,
    scheduledRunMinutes
  };
  // plannedTime is the total time that was planned to be running, i.e. not planned stop
  // *note* reportedMinutes, in ourlyOEE, may be 60 instead of the recorded amount
  const plannedTime = record.reportedMinutes - record.plannedStoppedMinutes;

  if (record.plannedMinutes > 0) {
    record.performance = OeeUtil.getPerformance(
      {
        totalCount: record.count,
        runTime: record.plannedMinutes,
        currentTargetCount: record.currentTargetCount,
        totalTime: record.reportedMinutes,
        ingestVersion: record.ingestVersion,
        plannedTime,
      },
    );

    record.availability = OeeUtil.getRecordAvailability(record);

    record.quality = OeeUtil.getQuality(record.count, record.badPieceCount);

    // OEE
    //   OEE = P * A * Q
    //   OEE = Performance * Availability * Quality (if available)
    record.oee = record.performance * record.availability * (record.quality || 1);

    // Simple projection
    record.currentRateSimple = record.count / plannedTime * 60;

    // update utilization
    const projectedRate = record.currentTargetCount / plannedTime * 60;
    // this is the actual amount produced this hour
    record.utilizationActual = record.count;
    // the amount that could have been produced during the stoppage
    record.utilizationPlanned = projectedRate * record.plannedStoppedMinutes;
    record.utilizationUnplanned = projectedRate * record.unplannedStoppedMinutes;
    // the rest (or zero) of the opportunity loss, what the shortfall from the ideal hour is
    record.utilizationLostOpportunity = Math.max(
      record.currentTargetCount
      - record.utilizationActual
      - record.utilizationPlanned
      - record.utilizationUnplanned,
      0,
    );
  }
  return record;
}
